import { useContext, useEffect, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { addDays, format } from 'date-fns';
import {
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  ClickAwayListener,
  TextField,
} from '@mui/material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { getInNewWindow } from '../../modules/fetch';
import { AccountDataContext } from '../../context/AccountDataContext';
import { useUpdateAccountSettingsMutation } from '../../api/accountAPI';
import { useSurveysQuery } from '../../api/surveyAPI';
import Error from '../../components/Error';
import {
  Container,
  Heading,
  Title,
  Copy,
  DateContainer,
  DateInput,
  StyledCalendarTodayIcon,
  StyledDateRange,
  Actions,
} from './styles';

const SupportReports = () => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [showCalendarAgentPerformance, setShowCalendarAgentPerformance] = useState(false);
  const [showCalendarSurvey, setShowCalendarSurvey] = useState(false);

  const [serverError, setServerError] = useState('');
  const [updateAccountSettings] = useUpdateAccountSettingsMutation();
  const [currentAccount, , , , reloadAccount] = useContext(AccountDataContext);
  const [report, setReport] = useState({
    period: '',
    email: '',
  });
  const [date, setDate] = useState([
    {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [dateAgentPerformance, setDateAgentPerformance] = useState([
    {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [dateSurvey, setDateSurvey] = useState([
    {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [surveyError, setSurveyError] = useState('');

  const {
    data: surveysData,
    isLoading: surveysLoading,
    error: surveysError,
  } = useSurveysQuery(true);

  const activeSurveys = useMemo(() => {
    return surveysData?.data?.filter((survey) => survey.status === 'active') || [];
  }, [surveysData]);

  const intl = useIntl();

  const handleDateRangeChange = (range) => {
    setDate([range.selection]);
    if (range.selection.startDate !== range.selection.endDate) {
      setShowCalendar(false);
    }
  };

  const handleDateRangeAgentPerformanceChange = (range) => {
    setDateAgentPerformance([range.selection]);
    if (range.selection.startDate !== range.selection.endDate) {
      setShowCalendarAgentPerformance(false);
    }
  };

  const handleDateAgentPerformanceClickAway = () => {
    setShowCalendarAgentPerformance(false);
  };

  const handleGenerate = () => {
    getInNewWindow(
      '/tickets/export',
      `dateFrom=${format(date[0].startDate, 'yyyy-MM-dd')} 00:00:00` +
        `&dateTo=${format(date[0].endDate, 'yyyy-MM-dd')} 23:59:59`,
    );
  };

  const handleGenerateAgentChatPerformance = () => {
    getInNewWindow(
      '/performances/export',
      `startDate=${format(dateAgentPerformance[0].startDate, 'yyyy-MM-dd')} 00:00:00` +
        `&endDate=${format(dateAgentPerformance[0].endDate, 'yyyy-MM-dd')} 23:59:59`,
    );
  };

  const handleDateRangeSurveyChange = (range) => {
    setDateSurvey([range.selection]);
    if (range.selection.startDate !== range.selection.endDate) {
      setShowCalendarSurvey(false);
    }
  };

  const handleDateSurveyClickAway = () => {
    setShowCalendarSurvey(false);
  };

  const handleGenerateSurveyReport = () => {
    getInNewWindow(
      '/surveys/report/export',
      `surveyId=${selectedSurveyId}&startDate=${format(
        dateSurvey[0].startDate,
        'yyyy-MM-dd',
      )} 00:00:00` + `&endDate=${format(dateSurvey[0].endDate, 'yyyy-MM-dd')} 23:59:59`,
    );
  };

  useEffect(() => {
    setReport(
      currentAccount.settings?.report?.automatically ?? {
        period: '',
        email: '',
      },
    );
  }, [currentAccount]);

  useEffect(() => {
    if (surveysError) {
      setSurveyError('Failed to load surveys');
    } else {
      setSurveyError('');
    }
  }, [surveysError]);

  const handleChange = (prop) => (event) => {
    const automatically = {
      [prop]: event.target.value,
    };
    setReport({ ...report, ...automatically });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setServerError('Updating');
    await updateAccountSettings({
      account: {
        settings: {
          report: { automatically: report },
        },
      },
    });
    setServerError('');
    reloadAccount();
  };

  return (
    <>
      {serverError && <Error message={serverError} />}
      <Container>
        <Heading>
          <Title>
            <FormattedMessage
              id="reports.title.customer_support_reports"
              defaultMessage="Customer Support Reports"
            />
          </Title>
        </Heading>
        <Actions>
          <Copy>
            <FormattedMessage
              id="reports.title.actions"
              defaultMessage="Please select the date range to generate the customer support report"
            />
          </Copy>
          <DateContainer>
            <DateInput onClick={() => setShowCalendar(!showCalendar)}>
              <StyledCalendarTodayIcon />
              {format(date[0].startDate, 'd MMMM y')} - {format(date[0].endDate, 'd MMMM y')}
            </DateInput>
            {showCalendar && (
              <StyledDateRange
                editableDateInputs={true}
                onChange={handleDateRangeChange}
                moveRangeOnFirstSelection={false}
                ranges={date}
                showDateDisplay={false}
              />
            )}
          </DateContainer>
          <Button variant="contained" color="primary" onClick={handleGenerate}>
            <FormattedMessage id="reports.actions.button" defaultMessage="Generate" />
          </Button>
        </Actions>
      </Container>
      <br />

      {(currentAccount?.viewsLevel ?? 0) < 4 && (
        <>
          <Container>
            <Heading>
              <Title>
                <FormattedMessage
                  id="reports.title.agent_chat_performance_reports"
                  defaultMessage="Agent Chat Performance Reports"
                />
              </Title>
            </Heading>
            <Actions>
              <Copy>
                <FormattedMessage
                  id="reports.title.actions_agent_chat_performance_reports"
                  defaultMessage="Please select the date range to generate the agent chat performance report"
                />
              </Copy>
              <ClickAwayListener onClickAway={handleDateAgentPerformanceClickAway}>
                <DateContainer>
                  <DateInput
                    onClick={() => setShowCalendarAgentPerformance(!showCalendarAgentPerformance)}
                  >
                    <StyledCalendarTodayIcon />
                    {format(dateAgentPerformance[0].startDate, 'd MMMM y')} -{' '}
                    {format(dateAgentPerformance[0].endDate, 'd MMMM y')}
                  </DateInput>
                  {showCalendarAgentPerformance && (
                    <StyledDateRange
                      editableDateInputs={true}
                      onChange={handleDateRangeAgentPerformanceChange}
                      moveRangeOnFirstSelection={false}
                      ranges={dateAgentPerformance}
                      showDateDisplay={false}
                    />
                  )}
                </DateContainer>
              </ClickAwayListener>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateAgentChatPerformance}
              >
                <FormattedMessage id="reports.actions.button" defaultMessage="Generate" />
              </Button>
            </Actions>
          </Container>
          <br />
        </>
      )}
      <Container>
        <Heading>
          <Title>
            <FormattedMessage id="reports.title.survey_reports" defaultMessage="Survey Reports" />
          </Title>
        </Heading>
        <Actions>
          <Copy>
            <FormattedMessage
              id="reports.title.actions_survey_reports"
              defaultMessage="Please select survey and date range to generate the survey report"
            />
          </Copy>
          {surveyError && <Error message={surveyError} />}
          <Box sx={{ minWidth: 300 }}>
            <FormControl fullWidth error={!!surveyError}>
              <InputLabel id="survey-select-label">
                <FormattedMessage id="reports.title.survey.label" defaultMessage="Survey" />
              </InputLabel>
              <Select
                labelId="survey-select-label"
                id="survey-select"
                label="survey-select"
                value={selectedSurveyId}
                onChange={(e) => setSelectedSurveyId(e.target.value)}
                disabled={surveysLoading}
              >
                <MenuItem value="">
                  <FormattedMessage
                    id="reports.title.survey.select"
                    defaultMessage="Select survey"
                  />
                </MenuItem>
                {activeSurveys.map((survey) => (
                  <MenuItem key={survey._id.$oid} value={survey._id.$oid}>
                    {survey.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <br />
          <ClickAwayListener onClickAway={handleDateSurveyClickAway}>
            <DateContainer>
              <DateInput onClick={() => setShowCalendarSurvey(!showCalendarSurvey)}>
                <StyledCalendarTodayIcon />
                {format(dateSurvey[0].startDate, 'd MMMM y')} -{' '}
                {format(dateSurvey[0].endDate, 'd MMMM y')}
              </DateInput>
              {showCalendarSurvey && (
                <StyledDateRange
                  editableDateInputs={true}
                  onChange={handleDateRangeSurveyChange}
                  moveRangeOnFirstSelection={false}
                  ranges={dateSurvey}
                  showDateDisplay={false}
                />
              )}
            </DateContainer>
          </ClickAwayListener>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateSurveyReport}
            disabled={!selectedSurveyId || surveysLoading}
          >
            <FormattedMessage id="reports.actions.button" defaultMessage="Generate" />
          </Button>
        </Actions>
      </Container>
      <br />
      <Container>
        <Heading>
          <Title>
            <FormattedMessage
              id="reports.title.automatically_generated_reports"
              defaultMessage="Automatically Generated Reports"
            />
          </Title>
        </Heading>
        <Actions>
          <Copy>
            <FormattedMessage
              id="reports.title.automatically.actions"
              defaultMessage="Please select the period generate the customer support report"
            />
          </Copy>
          <DateContainer>
            <Box sx={{ minWidth: 300 }}>
              <FormControl fullWidth>
                <InputLabel id="period-select-label">
                  <FormattedMessage
                    id="reports.title.automatically.period.label"
                    defaultMessage="Period"
                  />
                </InputLabel>
                <Select
                  labelId="period-select-label"
                  id={'period-select'}
                  label="period-select"
                  value={report.period}
                  onChange={handleChange('period')}
                >
                  <MenuItem value={'1d'}>
                    <FormattedMessage
                      id="reports.title.automatically.period.1d"
                      defaultMessage="Once a day"
                    />
                  </MenuItem>
                  <MenuItem value={'1w'}>
                    <FormattedMessage
                      id="reports.title.automatically.period.1w"
                      defaultMessage="Once a week"
                    />
                  </MenuItem>
                  <MenuItem value={'2w'}>
                    <FormattedMessage
                      id="reports.title.automatically.period.2w"
                      defaultMessage="Every 2 weeks"
                    />
                  </MenuItem>
                  <MenuItem value={'2m'}>
                    <FormattedMessage
                      id="reports.title.automatically.period.1m"
                      defaultMessage="Once a month"
                    />
                  </MenuItem>
                </Select>
              </FormControl>
              <br />
              <br />
              <FormControl fullWidth>
                <TextField
                  label={intl.formatMessage({
                    id: 'reports.title.automatically.email.label',
                    defaultMessage: 'E-Mail',
                  })}
                  id={'email-input'}
                  type={'email'}
                  value={report.email}
                  onChange={handleChange('email')}
                />
              </FormControl>
            </Box>
          </DateContainer>
          <Button variant="contained" color="primary" onClick={handleFormSubmit}>
            <FormattedMessage id="reports.actions.automatically.button" defaultMessage="Update" />
          </Button>
        </Actions>
      </Container>
    </>
  );
};

export default SupportReports;
